<script setup lang="ts">
/**
 * Fiche d'identité d'une association qui n'a encore rien publié (ni cagnotte,
 * ni événement, ni adhésion…). Remplace « Cette association n'a pas encore de
 * contenu public », qui faisait page vide pour les visiteurs comme pour Google
 * (Karim 26/10/2026 : les fiches restent indexées même avec peu d'infos —
 * on les ENRICHIT avec les faits déjà connus, registre officiel compris).
 *
 * N'affiche que des faits publics et vérifiables : catégorie, ville (lien
 * vers les associations de la ville), objet déclaré, date d'arrivée sur
 * Donoor. Ni RNA ni SIRET (Karim 26/10 : inutiles au visiteur ; Google les lit
 * dans les données structurées). Jamais la rue : le siège d'une petite
 * association est souvent un domicile.
 */
import { libelleLieuCourt } from "@donoor/utils";
import { villeOrganisation } from "#shared/utils/villeAnnuaire";
import { libellesCategorieAsso } from "~/utils/associationEntete";

const props = defineProps<{
  org: {
    name: string;
    category?: string | null;
    objet_social?: string | null;
    date_created?: string | null;
  } & Record<string, unknown>;
}>();

const { t, locale } = useI18n({ useScope: "local" });

const villeAnnuaire = computed(() => villeOrganisation(props.org as Parameters<typeof villeOrganisation>[0]));
const ville = computed(() => (villeAnnuaire.value ? libelleLieuCourt(villeAnnuaire.value) : null));
const lienVille = computed(() => (villeAnnuaire.value ? `/associations/ville/${villeAnnuaire.value.cle}` : null));
const categorie = computed(() =>
  props.org.category ? libellesCategorieAsso()[props.org.category] ?? null : null,
);
const depuis = computed(() => {
  if (!props.org.date_created) return null;
  const d = new Date(props.org.date_created);
  return Number.isNaN(d.getTime())
    ? null
    : new Intl.DateTimeFormat(locale.value, { month: "long", year: "numeric" }).format(d);
});
const objet = computed(() => props.org.objet_social?.trim() || null);

const lignes = computed(() =>
  [
    { cle: "categorie", valeur: categorie.value },
    { cle: "ville", valeur: ville.value },
    { cle: "depuis", valeur: depuis.value },
  ].filter((l): l is { cle: string; valeur: string } => Boolean(l.valeur)),
);
</script>

<template>
  <AssociationsPageChapitre
    id="section-identite"
    :titre="t('titre')"
    :texte="t('texte', { nom: org.name })"
    fond="gris"
    :defilant="false"
  >
    <div class="rounded-[28px] bg-white p-6 sm:p-10">
      <dl class="grid gap-x-10 gap-y-6 sm:grid-cols-2">
        <div v-for="ligne in lignes" :key="ligne.cle" class="flex flex-col gap-1">
          <dt class="text-[13px] font-semibold uppercase tracking-[0.06em] text-[#6e6e73]">{{ t(`champs.${ligne.cle}`) }}</dt>
          <dd class="m-0 text-[19px] font-semibold text-[#1d1d1f]">
            <NuxtLink v-if="ligne.cle === 'ville' && lienVille" :to="lienVille" class="hover:text-(--color-main) hover:underline">{{ ligne.valeur }}</NuxtLink>
            <template v-else>{{ ligne.valeur }}</template>
          </dd>
        </div>
        <div v-if="objet" class="flex flex-col gap-1 sm:col-span-2">
          <dt class="text-[13px] font-semibold uppercase tracking-[0.06em] text-[#6e6e73]">{{ t("champs.objet") }}</dt>
          <dd class="m-0 text-[17px] leading-relaxed text-[#424245]">{{ objet }}</dd>
        </div>
      </dl>
      <p class="mt-8 border-t border-[#e8e8ed] pt-6 text-[15px] text-[#6e6e73]">{{ t("bientot") }}</p>
    </div>
  </AssociationsPageChapitre>
</template>

<i18n lang="json" src="../../../i18n/pages/components/associations-page/AssociationsPageFicheIdentite.json"></i18n>
